// General
import "./support-form.scss";
import { Fragment, useEffect, useRef, useState } from "react";
// Services
import {
  useLazyGetProfileQuery,
  useLazyGetSupportConfigurationQuery,
  useSubmitSupportTicketMutation,
} from "../../../services/data.service";
import { emailRegex } from "../../../services/regex.service";
// Static Data
import user from "../../../enums/user";
import userConst from "../../../const/userConst";
import supportConst from "../../../const/supportConst";
import routeConst from "../../../const/routeConst";
// Redux
import { useSelector, useDispatch } from "react-redux";
import {
  updateSelectedPath,
  updateSupportFormObj,
} from "../../../redux/store/supportStore";
import {
  updateReportDialog,
  updateSupportFormDialog,
} from "../../../redux/store/dialogStore";
import {
  updateSuccessToast,
  updateErrorToast,
  updateWarningToast,
} from "../../../redux/store/toastStore";
// browser-image-compression
import imageCompression from "browser-image-compression";
// Material UI
import { ButtonBase, Button, MenuItem, TextField } from "@mui/material";
import UploadIcon from "@mui/icons-material/Upload";
import CloseIcon from "@mui/icons-material/Close";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
// i18next
import { useTranslation } from "react-i18next";
// Moment
import moment from "moment";
// react-gtm-module
import TagManager from "react-gtm-module";
// Custom Hooks
import useCustomNavigate from "../../utility/custom-hooks/useCustomNavigate-hook";
// Components
import Spinner from "../../shared/elements/spinner/spinner";

const SupportForm = (props) => {
  const { page } = props;

  const MAX_UPLOAD_COUNT = 3;

  // API variables
  const [
    getProfile,
    {
      data: getProfileData,
      error: getProfileErrorData,
      isFetching: getProfileFetching,
      isLoading: getProfileLoading,
      isSuccess: getProfileSuccess,
      isError: getProfileError,
    },
  ] = useLazyGetProfileQuery();
  const [
    getSupportConfiguration,
    {
      data: getSupportConfigurationData,
      error: getSupportConfigurationErrorData,
      isFetching: getSupportConfigurationFetching,
      isLoading: getSupportConfigurationLoading,
      isSuccess: getSupportConfigurationSuccess,
      isError: getSupportConfigurationIsError,
    },
  ] = useLazyGetSupportConfigurationQuery();
  const [
    submitSupportTicket,
    {
      data: submitSupportTicketData,
      error: submitSupportTicketErrorData,
      isLoading: submitSupportTicketLoading,
      isSuccess: submitSupportTicketSuccess,
      isError: submitSupportTicketError,
    },
  ] = useSubmitSupportTicketMutation();

  // General variables
  const minDate = moment().subtract(userConst.userAgeRange.min, "years");
  const maxDate = moment().subtract(userConst.userAgeRange.max, "years");
  const [imageData, setImageData] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedGender, setSelectedGender] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [selectedLookingFor, setSelectedLookingFor] = useState("");
  const [enteredEmail, setEnteredEmail] = useState("");
  const [enteredMobile, setEnteredMobile] = useState("");
  const [tellUsMore, setTellUsMore] = useState("");
  const [selectedDevice, setSelectedDevice] = useState("");
  const prevSelectedPathKeyRef = useRef("");

  // Redux variables
  const selectedPath = useSelector((state) => state.support.selectedPath);
  const reportDialogObj = useSelector((state) => state.support.reportDialogObj);
  const dispatch = useDispatch();

  // i18next variables
  const { t } = useTranslation();

  // Custom Hooks Functions
  const onNavigate = useCustomNavigate();

  // Lifecycle | Mounted
  useEffect(() => {
    getProfile(null, true);
    getSupportConfiguration(`?page=${page}`, true);
  }, []);

  // Lifecycle | Unmounted
  useEffect(() => {
    return () => {
      resetAllFormFields();
    };
  }, []);

  // Lifecycle | Check for update | selected category path
  useEffect(() => {
    const currentSelectedPathKey =
      selectedPath
        ?.map((path) => path?.key)
        ?.filter((key) => Boolean(key))
        ?.join(".") || "";
    const currentSelectedPathLastKey =
      selectedPath?.[selectedPath.length - 1]?.key || "";

    if (currentSelectedPathLastKey) {
      trackInteraction(currentSelectedPathLastKey);
    }

    if (prevSelectedPathKeyRef.current !== currentSelectedPathKey) {
      resetFormFields();
    }

    prevSelectedPathKeyRef.current = currentSelectedPathKey;
  }, [selectedPath]);

  // Lifecycle | Check for update | submitSupportTicket API Response
  useEffect(() => {
    if (submitSupportTicketLoading) {
    } else if (submitSupportTicketSuccess) {
      switch (submitSupportTicketData?.status) {
        case 0:
        case 1:
          const successToastObj = {
            message: submitSupportTicketData?.message || "",
            autoClose: 3000,
          };
          dispatch(updateSuccessToast(successToastObj));

          if (page === supportConst.page.reportUser) {
            dispatch(updateReportDialog(false));
          } else if (page === supportConst.page.vp3) {
            onNavigate(routeConst.vp3Support.endScreen.path);
          } else {
            onNavigate(-1);
          }
          break;
        default:
          break;
      }
    } else if (submitSupportTicketError) {
    }
  }, [
    submitSupportTicketLoading,
    submitSupportTicketSuccess,
    submitSupportTicketError,
  ]);

  // Event Handlers | Button
  const onCategoryClick = (level, data, title, placeholder) => {
    const obj = {
      title,
      placeholder,
      level,
      options: data,
    };
    dispatch(updateSupportFormObj(obj));
    dispatch(updateSupportFormDialog(true));
  };
  const onSubmit = () => {
    trackInteraction("submit-click");

    const selectedCategoryKey =
      selectedPath
        ?.map((path) => path?.key)
        ?.filter((key) => Boolean(key))
        ?.join(".") || null;

    const formData = new FormData();
    const appendIfNotNull = (key, value) => {
      if (value !== null && value !== undefined && value !== "") {
        formData.append(key, value);
      }
    };

    appendIfNotNull("key", selectedCategoryKey);
    appendIfNotNull("email", getProfileData?.data?.email);
    appendIfNotNull("name", getProfileData?.data?.username);
    formData.append("page", page || "contact_support");
    appendIfNotNull("data[calendar]", selectedDate);
    appendIfNotNull("data[description]", tellUsMore);
    appendIfNotNull("data[device]", selectedDevice);
    appendIfNotNull("data[email_input]", enteredEmail);
    appendIfNotNull("data[mobile_input]", enteredMobile);
    appendIfNotNull("data[gender]", selectedGender);
    appendIfNotNull("data[role]", selectedRole);
    appendIfNotNull("data[looking_for]", selectedLookingFor);
    appendIfNotNull("reported_user_id", reportDialogObj?.userId);

    imageData?.forEach((item, index) => {
      if (item?.file) {
        formData.append(`data[upload][${index}]`, item.file);
      }
    });

    // for (let [key, value] of formData.entries()) {
    //   console.log(key, value);
    // }
    submitSupportTicket(formData);
  };
  const onFilePicker = async (event) => {
    const options = {
      maxSizeMB: 1,
    };
    const files = Array.from(event.target.files || []);
    const remainingCount = MAX_UPLOAD_COUNT - imageData.length;
    const selectedFiles =
      remainingCount > 0 ? files.slice(0, remainingCount) : [];

    if (files.length > remainingCount) {
      const warningToastObj = {
        message: `Maximum ${MAX_UPLOAD_COUNT} files allowed`,
        autoClose: 3000,
      };
      dispatch(updateWarningToast(warningToastObj));
    }

    const nextUploadData = [];

    if (selectedFiles.length) {
      try {
        for (const file of selectedFiles) {
          if (file?.size > 1024 * 1024 * 10) {
            const warningToastObj = {
              message: t("common.file_size_is_too_large"),
              autoClose: 3000,
            };
            dispatch(updateWarningToast(warningToastObj));
            continue;
          }

          if (file.type.startsWith("image")) {
            const compressedFile = await imageCompression(file, options);
            const imagePreview = await readFile(compressedFile);
            nextUploadData.push({
              image: imagePreview,
              file: compressedFile,
            });
          } else if (file.type.startsWith("video")) {
            const thumbnail = await generateVideoThumbnail(file);
            nextUploadData.push({
              image: thumbnail,
              file,
            });
          }
        }

        if (nextUploadData.length) {
          setImageData((prevState) => [...prevState, ...nextUploadData]);
        }
      } catch (error) {
        console.log(error);
      }
    }

    event.target.value = "";
  };
  const onRemoveImage = (index) => {
    setImageData((prevState) => prevState.filter((_, i) => i !== index));
  };

  // Utility Functions
  const findSelectedItem = (items, key) => {
    return items?.find((item) => item?.key === key) || null;
  };
  const normalizeRoleForLookingForKey = (role) => {
    const normalizedRole = (role || "").toString().toLowerCase();

    if (normalizedRole === "sd") return "sugardaddy";
    if (normalizedRole === "sb") return "sugarbaby";

    return normalizedRole;
  };
  const readFile = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => {
        resolve(event.target.result);
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(file);
    });
  };
  const generateVideoThumbnail = (videoFile) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement("video");
      const videoUrl = URL.createObjectURL(videoFile);

      video.preload = "metadata";
      video.onloadedmetadata = async () => {
        try {
          const canvas = document.createElement("canvas");
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
          const ctx = canvas.getContext("2d");
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

          const thumbnail = canvas.toDataURL("image/jpeg");
          URL.revokeObjectURL(videoUrl);
          resolve(thumbnail);
        } catch (error) {
          URL.revokeObjectURL(videoUrl);
          reject(error);
        }
      };
      video.onerror = (error) => {
        URL.revokeObjectURL(videoUrl);
        reject(error);
      };
      video.src = videoUrl;
    });
  };
  const renderSupportForm = (data, level = 0, parentItem = null) => {
    if (!data?.length) return null;

    const selectedKey = selectedPath[level]?.key || "";
    const selectedItem = findSelectedItem(data, selectedKey);
    const hasChildren = !!selectedItem?.sub_category?.length;
    const faq = data?.faq ?? selectedItem?.faq ?? null;
    const isUploadEnabled = selectedItem?.fields?.upload?.enabled === true;
    const isUploadRequired = selectedItem?.fields?.upload?.required === true;
    const isCalendarEnabled = selectedItem?.fields?.calendar?.enabled === true;
    const isCalendarRequired =
      selectedItem?.fields?.calendar?.required === true;
    const isMobileInputEnabled =
      selectedItem?.fields?.mobile_input?.enabled === true;
    const isMobileInputRequired =
      selectedItem?.fields?.mobile_input?.required === true;
    const isRoleEnabled = selectedItem?.fields?.role?.enabled === true;
    const isRoleRequired = selectedItem?.fields?.role?.required === true;
    const isGenderEnabled = selectedItem?.fields?.gender?.enabled === true;
    const isGenderRequired = selectedItem?.fields?.gender?.required === true;
    const isLookingForEnabled =
      selectedItem?.fields?.looking_for && selectedGender === user.gender.LGBTQ;
    const isLookingForRequired =
      selectedItem?.fields?.looking_for?.required === true;
    const isEmailInputEnabled =
      selectedItem?.fields?.email_input?.enabled === true;
    const isEmailInputRequired =
      selectedItem?.fields?.email_input?.required === true;
    const isTellUsMoreEnabled =
      selectedItem?.fields?.description?.enabled === true;
    const isTellUsMoreRequired =
      selectedItem?.fields?.description?.required === true;
    const isDeviceEnabled = selectedItem?.fields?.device?.enabled === true;
    const isDeviceRequired = selectedItem?.fields?.device?.required === true;
    const placeholderText = selectedItem?.placeholder || "";
    const levelPlaceholder =
      parentItem?.placeholder ||
      t(level > 0 ? "support.select_subcategory" : "support.select_category");
    const levelDialogTitle =
      data?.find((item) => item?.sub_category_title)?.sub_category_title ||
      t(
        level === 0
          ? "support.what_can_we_help_with"
          : "support.lets_get_more_details"
      );
    const genderOptions = selectedItem?.fields?.gender?.options || [];
    const lookingForOptions = selectedItem?.fields?.looking_for?.options || [];
    const roleOptions = [
      userConst.wantToBe.sugardaddy,
      userConst.wantToBe.sugarbaby,
    ];
    const deviceOptions = selectedItem?.fields?.device?.options || [];

    const isFormValid = () => {
      if (submitSupportTicketLoading) return false;

      if (isUploadEnabled && isUploadRequired && !imageData?.length)
        return false;
      if (
        isCalendarEnabled &&
        isCalendarRequired &&
        (!selectedDate || !isBirthdateValid())
      )
        return false;
      if (isRoleEnabled && isRoleRequired && !selectedRole) return false;
      if (isGenderEnabled && isGenderRequired && !selectedGender) return false;
      if (isLookingForEnabled && isLookingForRequired && !selectedLookingFor)
        return false;
      if (
        isEmailInputEnabled &&
        isEmailInputRequired &&
        (!enteredEmail || !emailRegex?.test(enteredEmail))
      )
        return false;
      if (isTellUsMoreEnabled && isTellUsMoreRequired && !tellUsMore)
        return false;
      if (isDeviceEnabled && isDeviceRequired && !selectedDevice) return false;
      return true;
    };

    // const imagePreview = uploadedImagePreviews[level];
    const showSubmit =
      data?.submit === true || (!hasChildren && selectedItem?.submit === true);

    return (
      <Fragment key={`support-level-${level}`}>
        {level === 1 ? (
          <>
            <div style={{ padding: "10px" }} />

            <div className="support-title">
              {t("support.lets_get_more_details")}
            </div>
          </>
        ) : null}

        <div style={{ padding: "5px" }} />

        <div
          className="support-text-field"
          onClick={() =>
            onCategoryClick(level, data, levelDialogTitle, levelPlaceholder)
          }
        >
          <div
            className={`support-text-field-label`}
            style={{
              fontStyle: selectedPath[level]?.label ? "normal" : "italic",
              color: selectedPath[level]?.label ? "black" : "grey",
            }}
          >
            {selectedPath[level]?.label || levelPlaceholder}
          </div>

          <NavigateNextIcon />
        </div>

        {hasChildren
          ? renderSupportForm(
              selectedItem.sub_category,
              level + 1,
              selectedItem
            )
          : null}

        {isCalendarEnabled ? (
          <div className="calendar-container">
            <div style={{ padding: "10px" }} />

            <div className="calendar-title">{t("support.enter_your_age")}</div>

            <div style={{ padding: "5px" }} />

            <input
              type="date"
              className="support-date-input"
              value={selectedDate || ""}
              placeholder={placeholderText}
              onChange={(event) => {
                trackInteraction("select-birthdate");
                setSelectedDate(event.target.value);
              }}
              min={moment().subtract(70, "years").format("YYYY-MM-DD")}
              max={moment().subtract(18, "years").format("YYYY-MM-DD")}
              aria-label="date-picker"
            />

            {!isBirthdateValid() && (
              <div className="dob-hint">{t("support.date_of_birth_hint")}</div>
            )}
          </div>
        ) : null}

        {isGenderEnabled ? (
          <div className="gender-container">
            <div style={{ padding: "10px" }} />

            <div className="gender-title">{t("support.change_gender")}</div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-gender-picker"
              fullWidth
              select
              value={selectedGender}
              placeholder={placeholderText}
              onChange={(event) => {
                trackInteraction("select-gender");
                setSelectedGender(event.target.value);
                setSelectedLookingFor("");
                setSelectedRole("");
              }}
            >
              {genderOptions?.map((option, index) => (
                <MenuItem
                  value={option?.key}
                  disabled={
                    (getProfileData?.data?.gender === user.gender.MAN ||
                      getProfileData?.data?.gender === user.gender.WOMAN) &&
                    getProfileData?.data?.gender === option?.key
                  }
                  key={`${option?.key}-${index}`}
                >
                  {t(option?.label)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        ) : null}

        {isLookingForEnabled ? (
          <div className="looking-for-container">
            <div style={{ padding: "10px" }} />

            <div className="looking-for-title">
              {t("support.change_looking_for")}
            </div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-looking-for-picker"
              fullWidth
              select
              value={selectedLookingFor}
              placeholder={placeholderText}
              onChange={(event) => {
                trackInteraction("select-looking-for");
                setSelectedLookingFor(event.target.value);
                setSelectedRole("");
              }}
            >
              {lookingForOptions?.map((option, index) => (
                <MenuItem value={option?.key} key={`${option?.key}-${index}`}>
                  {t(option?.label)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        ) : null}

        {isRoleEnabled ? (
          <div className="screenshot-upload-container">
            <div style={{ padding: "10px" }} />

            <div className="screenshot-title">{t("support.change_role")}</div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-role-picker"
              fullWidth
              select
              value={selectedRole}
              placeholder={placeholderText}
              onChange={(event) => {
                trackInteraction("select-role");
                setSelectedRole(event.target.value);
              }}
            >
              {roleOptions.map((option, index) => (
                <MenuItem
                  value={option?.value}
                  disabled={
                    (selectedGender === user.gender.MAN ||
                      selectedGender === user.gender.WOMAN) &&
                    normalizeRoleForLookingForKey(
                      getProfileData?.data?.role
                    ) === (option?.value || "").toString().toLowerCase()
                  }
                  key={`${option?.value}-${index}`}
                >
                  {t(option?.title)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        ) : null}

        {isEmailInputEnabled ? (
          <div className="email-container">
            <div style={{ padding: "10px" }} />

            <div className="email-title">{t("support.sugarbook_email")}</div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-email-input"
              fullWidth
              type="email"
              value={enteredEmail}
              placeholder={placeholderText}
              onChange={(event) => setEnteredEmail(event.target.value)}
              onBlur={() => trackInteraction("input-email")}
            />
          </div>
        ) : null}

        {isDeviceEnabled ? (
          <div className="support-device-container">
            <div style={{ padding: "10px" }} />

            <div className="device-title">{t("support.device")}</div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-device-picker"
              fullWidth
              select
              value={selectedDevice}
              placeholder={placeholderText}
              onChange={(event) => {
                trackInteraction("select-device");
                setSelectedDevice(event.target.value);
              }}
            >
              {deviceOptions?.map((option, index) => (
                <MenuItem value={option?.key} key={index}>
                  {t(option?.label)}
                </MenuItem>
              ))}
            </TextField>
          </div>
        ) : null}

        {isTellUsMoreEnabled && (
          <div className="support-tell-us-more-container">
            <div style={{ padding: "10px" }} />

            <div className="tell-us-more-title">
              {t("support.tell_us_more_title")} (
              {t(
                isTellUsMoreRequired ? "support.required" : "support.optional"
              )}
              )
            </div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-tell-us-more-input"
              multiline
              minRows={3}
              fullWidth
              placeholder={placeholderText}
              value={tellUsMore}
              onChange={(event) => setTellUsMore(event.target.value)}
              onBlur={() => trackInteraction("input-description")}
            />
          </div>
        )}

        {isMobileInputEnabled ? (
          <div className="mobile-input-container">
            <div style={{ padding: "10px" }} />

            <div className="mobile-input-title">
              {t("support.mobile_number")}
            </div>

            <div style={{ padding: "5px" }} />

            <TextField
              className="support-mobile-input"
              fullWidth
              type="email"
              value={enteredMobile}
              placeholder={placeholderText}
              onChange={(event) => setEnteredMobile(event.target.value)}
              onBlur={() => trackInteraction("input-mobile")}
            />
          </div>
        ) : null}

        {isUploadEnabled ? (
          <div className="screenshot-upload-container">
            <div style={{ padding: "10px" }} />

            <div className="screenshot-title">
              {t("support.screenshot_upload_title")} (
              {t(isUploadRequired ? "support.required" : "support.optional")})
            </div>

            <div style={{ padding: "5px" }} />

            <Button
              className="upload-screenshot-container"
              disableRipple
              component="label"
            >
              {imageData?.length ? (
                <div className="upload-preview-list-container">
                  <div className="upload-preview-list">
                    {imageData?.map((item, index) => (
                      <div
                        className="preview-container"
                        key={`${item?.file?.name}-${index}`}
                      >
                        <img
                          className="upload-screenshot-image"
                          src={item?.image}
                          alt="upload-screenshot"
                        />
                        <div
                          className="close-icon-button"
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            onRemoveImage(index);
                          }}
                        >
                          <CloseIcon className="close-icon" />
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="upload-screenshot-label">
                    <input
                      hidden
                      accept="image/*, video/*"
                      type="file"
                      multiple
                      onChange={onFilePicker}
                    />
                    <UploadIcon className="upload-icon" />
                    <div className="upload-screenshot-text">
                      {t("settings.support_form.upload_screenshot")} (
                      {imageData.length}/{MAX_UPLOAD_COUNT})
                    </div>
                  </div>
                </div>
              ) : (
                <div className="upload-screenshot-label">
                  <input
                    hidden
                    accept="image/*, video/*"
                    type="file"
                    multiple
                    onChange={onFilePicker}
                  />
                  <UploadIcon className="upload-icon" />
                  <div className="upload-screenshot-text">
                    {t("settings.support_form.upload_screenshot")} (0/
                    {MAX_UPLOAD_COUNT})
                  </div>
                </div>
              )}
            </Button>
          </div>
        ) : null}

        {faq !== null ? (
          <div className="faq-container">
            <div
              dangerouslySetInnerHTML={{
                __html: (faq || "").replace(/\\n|\n/g, "<br />"),
              }}
            />
          </div>
        ) : null}

        {showSubmit ? (
          <ButtonBase
            className={`submit-button ${!isFormValid() ? "disabled-button" : ""}`}
            component="div"
            disabled={!isFormValid()}
            onClick={onSubmit}
          >
            {submitSupportTicketLoading ? (
              <Spinner isPadding={false} size={20} />
            ) : (
              t("common.submit")
            )}
          </ButtonBase>
        ) : null}
      </Fragment>
    );
  };
  const resetFormFields = () => {
    setImageData([]);
    setSelectedDate("");
    setSelectedRole("");
    setEnteredEmail("");
    setTellUsMore("");
    setSelectedDevice("");
  };
  const resetAllFormFields = () => {
    setImageData([]);
    setSelectedDate("");
    setSelectedRole("");
    setEnteredEmail("");
    setTellUsMore("");
    setSelectedDevice("");
    dispatch(updateSupportFormObj(null));
    dispatch(updateSupportFormDialog(false));
    dispatch(updateSelectedPath([]));
  };
  const isBirthdateValid = () => {
    const minDate = moment().subtract(18, "years");
    const maxDate = moment().subtract(70, "years");
    const selectedBirthdate = moment(selectedDate);

    return selectedBirthdate.isBetween(maxDate, minDate);
  };
  const trackInteraction = (key) => {
    if (!key) return;

    TagManager.dataLayer({
      dataLayer: {
        event: `PWA-66-${key}`,
      },
    });
  };

  return (
    <div id="support-form-container">
      <div className="support-title">{t("support.title")}</div>

      {renderSupportForm(getSupportConfigurationData?.data)}
    </div>
  );
};

export default SupportForm;
